// BLEZZINGS Growth Tools & Resources — registry core.
// Codes, categories, helpers. Tool entries live in toolsGP/MC/SB/DI.js.
// Binding spec: 104 resources, fixed IDs/slugs/owners. CI check:
// scripts/test_tools_registry.mjs asserts counts, uniqueness and valid refs.

export const TOOL_CODES = {
  // Strategy & Commercial Transformation
  GS: "/capabilities/strategy-commercial-transformation/growth-strategy",
  PP: "/capabilities/strategy-commercial-transformation/product-portfolio-strategy",
  PR: "/capabilities/strategy-commercial-transformation/pricing-revenue-management",
  GTM: "/capabilities/strategy-commercial-transformation/go-to-market-market-entry",
  CH: "/capabilities/strategy-commercial-transformation/channel-route-to-market",
  ST: "/capabilities/strategy-commercial-transformation/sales-transformation",
  CE: "/capabilities/strategy-commercial-transformation/commercial-excellence-operating-model",
  // Market & Customer Intelligence
  MO: "/capabilities/market-customer-intelligence/market-opportunity-sizing",
  CI: "/capabilities/market-customer-intelligence/customer-buyer-insights",
  SEG: "/capabilities/market-customer-intelligence/segmentation-targeting",
  COMP: "/capabilities/market-customer-intelligence/competitive-intelligence",
  CJ: "/capabilities/market-customer-intelligence/customer-decision-journey-intelligence",
  TF: "/capabilities/market-customer-intelligence/trends-foresight-growth-opportunities",
  // Brand, Marketing & Communications
  BP: "/capabilities/brand-marketing-communications/brand-strategy-positioning",
  BA: "/capabilities/brand-marketing-communications/brand-architecture-portfolio",
  BI: "/capabilities/brand-marketing-communications/brand-identity-design-systems",
  MS: "/capabilities/brand-marketing-communications/marketing-strategy-integrated-communications",
  AC: "/capabilities/brand-marketing-communications/advertising-content-creative",
  PRC: "/capabilities/brand-marketing-communications/public-relations-reputation",
  // Digital & Commerce
  PM: "/capabilities/digital-commerce/performance-marketing-paid-media",
  EC: "/capabilities/digital-commerce/ecommerce-marketplace-growth",
  CRO: "/capabilities/digital-commerce/d2c-cro-digital-experience",
  CRM: "/capabilities/digital-commerce/crm-retention-loyalty",
  SEO: "/capabilities/digital-commerce/seo-content-ai-search",
  SOC: "/capabilities/digital-commerce/social-influencer-creator-marketing",
  AMZ: "/capabilities/digital-commerce/amazon-marketplace-growth",
  DPM: "/capabilities/digital-commerce/d2c-performance-marketing",
  // Industries
  CD: "/industries/consumer-durables-electronics-appliances",
  FMCG: "/industries/fmcg-consumer-products",
  RET: "/industries/retail-d2c-ecommerce",
  MFG: "/industries/manufacturing-b2b",
  EDU: "/industries/education-edtech",
  MED: "/industries/media-entertainment",
  RE: "/industries/real-estate",
  PS: "/industries/professional-services",
  // Specials
  STRAT: "/capabilities/strategy-commercial-transformation",
  DIGITAL: "/capabilities/digital-commerce",
  Home: "/",
  Leadership: "/leadership",
};

export const TOOL_CATEGORIES = [
  { id: "growth-strategy", name: "Growth & Strategy", prefix: "G", count: 12 },
  { id: "pricing-profitability", name: "Pricing, Profitability & Portfolio", prefix: "P", count: 14 },
  { id: "market-intelligence", name: "Market & Customer Intelligence", prefix: "M", count: 13 },
  { id: "gtm-distribution", name: "Go-to-Market & Distribution", prefix: "C", count: 14 },
  { id: "sales-excellence", name: "Sales & Commercial Excellence", prefix: "S", count: 14 },
  { id: "brand-marketing", name: "Brand, Marketing & Communications", prefix: "B", count: 13 },
  { id: "digital-commerce", name: "Digital & Commerce", prefix: "D", count: 16 },
  { id: "industry", name: "Industry Solutions", prefix: "I", count: 8 },
];

export const TOOL_TYPES = {
  diagnostic: "Diagnostic",
  calculator: "Calculator",
  simulator: "Simulator",
  planner: "Planner",
  assessment: "Assessment",
  kit: "Kit & Templates",
  library: "Library",
  game: "Interactive Game",
};

export const codeUrl = (code) => TOOL_CODES[code];
export const toolUrl = (t) => `/tools/${t.slug}`;

export function resolveTool(t) {
  return {
    ...t,
    url: toolUrl(t),
    primaryUrl: codeUrl(t.primary),
    secondaryUrls: (t.secondary || []).map(codeUrl),
  };
}

export const categoryOf = (id) => TOOL_CATEGORIES.find((c) => c.id === id);

import { TOOLS_GP } from "./toolsGP";
import { TOOLS_MC } from "./toolsMC";
import { TOOLS_SB } from "./toolsSB";
import { TOOLS_DI } from "./toolsDI";

// The complete approved registry — 104 resources (integrity asserted in CI).
export const TOOLS = [...TOOLS_GP, ...TOOLS_MC, ...TOOLS_SB, ...TOOLS_DI].map(resolveTool);

export const PUBLISHED_TOOLS = TOOLS.filter((t) => t.status === "published");
export const toolBySlug = (slug) => TOOLS.find((t) => t.slug === slug);
export const publishedBySlug = (slug) => PUBLISHED_TOOLS.find((t) => t.slug === slug);

// All published tools whose PRIMARY owner is a given page URL.
export const toolsOwnedBy = (pageUrl) =>
  PUBLISHED_TOOLS.filter((t) => t.primaryUrl === pageUrl);
// The single featured tool for a page (spec §4), if published.
// Practice hubs own no tool directly — fall back to the hub's tool category
// so every practice page can surface a relevant free tool.
const PRACTICE_HUB_CATEGORY = {
  "/capabilities/strategy-commercial-transformation": "growth-strategy",
  "/capabilities/market-customer-intelligence": "market-intelligence",
  "/capabilities/brand-marketing-communications": "brand-marketing",
  "/capabilities/digital-commerce": "digital-commerce",
};
export const featuredToolFor = (pageUrl) =>
  PUBLISHED_TOOLS.find((t) => t.primaryUrl === pageUrl && t.featured) ||
  toolsOwnedBy(pageUrl)[0] ||
  PUBLISHED_TOOLS.find((t) => t.featured && t.category === PRACTICE_HUB_CATEGORY[pageUrl]) ||
  null;
// Published tools listing this page as a SECONDARY link.
export const toolsSecondaryTo = (pageUrl) =>
  PUBLISHED_TOOLS.filter((t) => t.secondaryUrls.includes(pageUrl));
// Published tools in a category.
export const toolsInCategory = (catId) =>
  PUBLISHED_TOOLS.filter((t) => t.category === catId);
